/**
 * Position management for tracking user holdings in prediction markets.
 */

import type { Position, Trade, Market, MarketOutcome, Order } from './types'

/**
 * Calculate how many Yes shares the user can still sell in a market.
 * Open sell orders reserve shares until they fill or are canceled.
 */
export function getAvailableYesShares(
  positions: Position[],
  marketId: string,
  openOrders: Order[] = []
): number {
  const yesShares = positions
    .filter((position) => position.marketId === marketId && position.outcome === 'Yes')
    .reduce((total, position) => total + position.shares, 0)

  const reservedShares = openOrders
    .filter(
      (order) =>
        order.side === 'sell' &&
        (order.status === 'open' || order.status === 'partial')
    )
    .reduce((total, order) => total + (order.quantity - order.filledQuantity), 0)

  return Math.max(0, yesShares - reservedShares)
}

/**
 * Update user positions after a trade executes.
 *
 * NOTE: MVP LIMITATION - Currently only 'Yes' shares are supported.
 * In prediction markets, buying 'Yes' at $0.70 is equivalent to selling 'No' at $0.30.
 * For simplicity, this MVP only tracks 'Yes' positions. Trading 'No' shares
 * (which would be the complement) will be added in a future version.
 *
 * @param positions - Current array of user positions (will be mutated)
 * @param trade - The trade that was executed
 * @param userOrderId - ID of the user's order in the trade
 * @param market - The market where the trade occurred
 * @returns The updated positions array
 */
export function updatePositions(
  positions: Position[],
  trade: Trade,
  userOrderId: string,
  market: Market
): Position[] {
  const userIsBuyer = trade.buyOrderId === userOrderId
  const outcome: MarketOutcome = 'Yes' // MVP: Only 'Yes' shares supported

  const existingPosition = positions.find(
    p => p.marketId === market.id && p.outcome === outcome
  )

  if (userIsBuyer) {
    // Buying shares - increase position
    if (existingPosition) {
      // Update average cost using weighted average formula
      const totalCost = existingPosition.averageCost * existingPosition.shares + trade.price * trade.quantity
      const totalShares = existingPosition.shares + trade.quantity
      existingPosition.averageCost = totalCost / totalShares
      existingPosition.shares = totalShares
    } else {
      // Create new position
      positions.push({
        marketId: market.id,
        outcome,
        shares: trade.quantity,
        averageCost: trade.price,
        realizedPnL: 0,
      })
    }
  } else {
    // Selling shares - decrease position
    if (existingPosition && existingPosition.shares >= trade.quantity) {
      // Calculate realized P&L: (sell price - average cost) * quantity
      const pnl = (trade.price - existingPosition.averageCost) * trade.quantity
      existingPosition.realizedPnL += pnl
      existingPosition.shares -= trade.quantity

      // Remove position if fully closed
      if (existingPosition.shares === 0) {
        const index = positions.indexOf(existingPosition)
        positions.splice(index, 1)
      }
    }
    // Note: If user tries to sell more than they own, the trade shouldn't happen
    // This is enforced at the order validation level, not here
  }

  return positions
}
